/* ============================================================================
 * THE SPEAKING PRESENTATION — the master choice, and what it actually needs.
 *
 * The deck has two ways to be delivered, and they are different products:
 *
 *   READING   — the reader sets the pace. The speaker note is a prompt they read
 *               silently. This is the review mode, and it is what the deck already did.
 *   SPEAKING  — the deck sets the pace. The note is spoken aloud and shown as a
 *               subtitle, and the deck advances when the sentence ends.
 *
 * TWO ENGINES, AND THE RECORDED ONE WINS (PO 2026-10-09: *"the tts is terrible"*).
 *
 *   1. RECORDED CLIPS — `/pitch/voice/note-NN.mp3`, one per slide. If a clip exists for the
 *      slide on screen it plays, and the subtitle still tracks it. **This is the fix.**
 *   2. THE BROWSER ENGINE — `speechSynthesis`, whose `boundary` event reports the character
 *      index reached, which is how the subtitle stays in sync. **This is the fallback**, and
 *      it is what "terrible" refers to: it is whatever voice the machine happens to have.
 *
 * The fallback exists so the mode is testable TODAY, and so a missing clip degrades to a
 * worse voice rather than to silence. Dropping the fifteen files in is the whole switch —
 * no code below changes.
 * ========================================================================== */

import { useCallback, useEffect, useRef, useState } from 'react';

export type DeckMode = 'reading' | 'speaking';

/** Feature detection, not a user-agent guess — a browser without the API gets no button. */
export function speechAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

/**
 * A voice, chosen deterministically. `getVoices()` is empty until the engine has loaded, which
 * is why the caller may get `null` and must not treat that as a failure — the utterance still
 * speaks with the platform default, and the second attempt has voices.
 */
function pickVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const english = voices.filter((v) => /^en/i.test(v.lang));
  const pool = english.length ? english : voices;
  /* A LOCAL voice is preferred because a network voice can stall mid-sentence on a slow
     connection, and a stalled subtitle that never advances is worse than a plainer one. */
  return pool.find((v) => v.localService) ?? pool[0] ?? null;
}

/**
 * Split into words WITH their character offsets, so a boundary index can be mapped back to a
 * word without re-scanning the string on every event. `\s` runs are kept in the offsets so the
 * maths stays exact; only the tokens are returned.
 */
export function wordsWithOffsets(text: string): Array<{ w: string; i: number }> {
  const out: Array<{ w: string; i: number }> = [];
  for (const m of text.matchAll(/\S+/g)) out.push({ w: m[0], i: m.index ?? 0 });
  return out;
}

/** Where recorded notes live. Numbered from 01 to match the deck's own slide order. */
const VOICE_DIR = '/pitch/voice';
export const voiceClipPath = (slideNumber: number): string =>
  `${VOICE_DIR}/note-${String(slideNumber).padStart(2, '0')}.mp3`;

export type Speech = {
  supported: boolean;
  speaking: boolean;
  /** Character index the synthesiser has reached, or -1 when idle. */
  at: number;
  /**
   * A play that produced no sound. `speechSynthesis` EXISTS on every browser that implements the
   * spec, but a machine with no voices installed installs no voice to speak with — so the API
   * check passes and the button still does nothing. That is the silent-failure shape this deck
   * keeps finding, so it is detected and surfaced instead of left to the reader to guess.
   */
  failed: boolean;
  /** True while a RECORDED clip is playing, so the UI can say which engine is speaking. */
  recorded: boolean;
  /** Play the slide's recorded clip. Resolves false when there is none and the caller should fall back. */
  playClip: (slideNumber: number, onDone?: () => void) => Promise<boolean>;
  speak: (text: string, onDone?: () => void) => void;
  stop: () => void;
};

export function useSpeech(): Speech {
  const supported = speechAvailable();
  const [speaking, setSpeaking] = useState(false);
  const [at, setAt] = useState(-1);
  const [failed, setFailed] = useState(false);
  const utter = useRef<SpeechSynthesisUtterance | null>(null);
  const done = useRef<(() => void) | undefined>(undefined);

  const stop = useCallback(() => {
    if (!supported) return;
    done.current = undefined;
    /* `cancel()` fires the utterance's own `onend` in some engines — so the callback is cleared
       FIRST. Otherwise a manual stop would walk the deck forward one slide. */
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setAt(-1);
  }, [supported]);

  /* The recorded clip for the slide on screen, if one has been dropped in. */
  const audio = useRef<HTMLAudioElement | null>(null);
  const [recorded, setRecorded] = useState(false);

  const stopAudio = useCallback(() => {
    const a = audio.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
      audio.current = null;
    }
    setRecorded(false);
  }, []);

  /**
   * Play a recorded clip if the slide has one. Resolves `false` when there is no clip (a 404,
   * or a decode failure), so the caller can fall back to the browser engine — the deck must not
   * go silent merely because the assets have not been recorded yet.
   */
  const playClip = useCallback(
    (slideNumber: number, onDone?: () => void): Promise<boolean> =>
      new Promise((resolve) => {
        const a = new Audio(voiceClipPath(slideNumber));
        let settled = false;
        const fail = () => {
          if (settled) return;
          settled = true;
          audio.current = null;
          setRecorded(false);
          resolve(false);
        };
        a.oncanplaythrough = () => {
          if (settled) return;
          settled = true;
          audio.current = a;
          done.current = onDone;
          setRecorded(true);
          setSpeaking(true);
          void a.play().catch(fail);
          resolve(true);
        };
        a.onended = () => {
          setSpeaking(false);
          setRecorded(false);
          const f = done.current;
          done.current = undefined;
          f?.();
        };
        a.onerror = fail;
        /* a clip that never loads must not hang the run */
        window.setTimeout(() => { if (!settled) fail(); }, 2500);
      }),
    [],
  );

  const speak = useCallback(
    (text: string, onDone?: () => void) => {
      if (!supported || !text.trim()) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      /* THE WATCHDOG. `onstart` is the engine saying it actually began — and it is the only
         signal that can tell a working voice from an absent one, because `speak()` itself
         returns nothing either way. If it has not fired shortly after, this machine cannot
         speak; say so rather than leaving a control that appears live. */
      const watchdog = window.setTimeout(() => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setFailed(true);
        done.current = undefined;
      }, 2200);
      u.onstart = () => window.clearTimeout(watchdog);
      const v = pickVoice();
      if (v) u.voice = v;
      /* Slower than conversational: a listener reading a subtitle while hearing it needs the
         two to stay together, and every engine drifts if it is pushed. */
      u.rate = 0.95;
      u.pitch = 1;
      u.onboundary = (e) => setAt(e.charIndex);
      u.onend = () => {
        window.clearTimeout(watchdog);
        setSpeaking(false);
        setAt(-1);
        const f = done.current;
        done.current = undefined;
        f?.();
      };
      u.onerror = () => {
        window.clearTimeout(watchdog);
        setSpeaking(false);
        setAt(-1);
        setFailed(true);
        done.current = undefined;
      };
      utter.current = u;
      done.current = onDone;
      setSpeaking(true);
      setAt(-1);
      window.speechSynthesis.speak(u);
    },
    [supported],
  );

  /* Leaving the deck mid-sentence must not leave a voice talking over the next page. */
  useEffect(
    () => () => {
      if (supported) window.speechSynthesis.cancel();
      const a = audio.current;
      if (a) a.pause();
    },
    [supported],
  );

  /* `stop` must silence BOTH engines — a recorded clip left playing over a paged deck is the
     same fault the browser path already guards against. */
  const stopAll = useCallback(() => {
    stopAudio();
    stop();
  }, [stop, stopAudio]);

  return { supported, speaking, at, failed, recorded, playClip, speak, stop: stopAll };
}
